import { useState, useEffect, useCallback, useRef } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { 
  ArrowLeft, 
  Upload, 
  Folder, 
  FileText, 
  Image as ImageIcon, 
  Trash2,
  Edit,
  Save,
  X,
  Plus,
  FolderPlus,
  ChevronRight,
  ChevronDown,
  Instagram,
  Facebook,
  CheckCircle,
  AlertCircle
} from "lucide-react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "@/lib/toast";
import { 
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { supabase } from "@/integrations/supabase/client";
import { Table, TableHeader, TableRow, TableHead, TableBody, TableCell } from "@/components/ui/table";
import { FilesContextMenu } from "@/components/clients/FilesContextMenu";
import { CreateFolderDialog } from "@/components/clients/CreateFolderDialog";
import { ClientFile, ClientFolder } from "@/types/database";

type ClientType = {
  id: string;
  name: string;
  email: string;
  phone: string;
  address: string;
  contactName: string;
  contactPosition: string;
  status: string;
  logoUrl: string | null;
  logoInitials: string;
  socialAccounts: number;
  pendingTasks: number;
  nextDelivery: string | null;
};

type SocialAccount = {
  id: string;
  platform: string;
  account_name: string;
  account_url?: string;
  followers_count?: number;
  created_at: string;
  access_token?: string;
};

export default function ClientDetail() {
  
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const clientId = id;
  const fileInputRef = useRef<HTMLInputElement>(null);
  
  const [client, setClient] = useState<ClientType | null>(null);
  
  const [clientData, setClientData] = useState({
    name: "",
    email: "",
    phone: "",
    address: "",
    contactName: "",
    contactPosition: "",
  });
  
  const [clientFiles, setClientFiles] = useState<ClientFile[]>([]);
  const [clientFolders, setClientFolders] = useState<ClientFolder[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isEditing, setIsEditing] = useState(false);
  const [logoFile, setLogoFile] = useState<File | null>(null);
  const [logoPreview, setLogoPreview] = useState<string | null>(null);
  const [currentFolderId, setCurrentFolderId] = useState<string | null>(null);
  const [breadcrumbs, setBreadcrumbs] = useState<{id: string | null, name: string}[]>([{id: null, name: 'Raíz'}]);
  const [isFolderDialogOpen, setIsFolderDialogOpen] = useState(false);
  const [isDragging, setIsDragging] = useState(false);

  const [socialAccounts, setSocialAccounts] = useState<SocialAccount[]>([]);
  const [isLoadingSocial, setIsLoadingSocial] = useState(false);
  
  // Fetch client data
  const fetchClientData = async () => {
    if (!clientId) return;
    
    setIsLoading(true);
    try {
      console.log("Buscando dados do cliente:", clientId);
      
      // Fetch client details
      const { data: clientData, error: clientError } = await supabase
        .from('clients')
        .select('*')
        .eq('id', clientId)
        .single();
        
      if (clientError) {
        console.error("Erro ao buscar cliente:", clientError);
        throw clientError;
      }
      
      if (!clientData) {
        console.log("Cliente não encontrado, redirecionando...");
        navigate("/clients");
        return;
      }
      
      console.log("Dados do cliente encontrados:", clientData);
      
      // Transform client data
      const formattedClient: ClientType = {
        id: clientData.id,
        name: clientData.name,
        email: clientData.email || "",
        phone: clientData.phone || "",
        address: clientData.address || "",
        contactName: clientData.contact_name || "",
        contactPosition: clientData.contact_position || "",
        status: clientData.status || "",
        logoUrl: clientData.logo_url,
        logoInitials: clientData.logo_initials || clientData.name.substring(0, 2).toUpperCase(),
        socialAccounts: clientData.social_accounts || 0,
        pendingTasks: clientData.pending_tasks || 0,
        nextDelivery: clientData.next_delivery
      };
      
      setClient(formattedClient);
      setClientData({
        name: formattedClient.name,
        email: formattedClient.email,
        phone: formattedClient.phone,
        address: formattedClient.address,
        contactName: formattedClient.contactName,
        contactPosition: formattedClient.contactPosition,
      });
      setLogoPreview(formattedClient.logoUrl);
    } catch (error: any) {
      console.error('Error fetching client data:', error);
      toast.error(`Erro ao carregar dados do cliente: ${error.message}`);
      navigate("/clients");
    } finally {
      setIsLoading(false);
    }
  };
  
  // Fetch files and folders
  const fetchFilesAndFolders = useCallback(async (folderId: string | null = null) => {
    if (!clientId) return;

    try {
      console.log('Fetching folders for client:', clientId, 'folderId:', folderId);

      // Chamada RPC corrigida para lidar com valores nulos
      const { data: foldersData, error: foldersError } = await supabase.rpc(
        'get_client_folders', 
        { 
          p_client_id: clientId, 
          p_parent_folder_id: folderId === null ? null : folderId 
        }
      );

      if (foldersError) {
        console.error('Error fetching folders:', foldersError);
        toast.error(`Erro ao carregar pastas: ${foldersError.message}`);
        return;
      }

      console.log('Fetched folders:', foldersData);

      // Fetch files com filtro correto para valores nulos
      let filesQuery = supabase
        .from("client_files")
        .select("*")
        .eq("client_id", clientId);

      // Aplicar filtro de pasta corretamente
      if (folderId === null) {
        // Para a raiz, buscar arquivos sem pasta (folder_id é nulo)
        filesQuery = filesQuery.is("folder_id", null);
      } else {
        // Para uma pasta específica, buscar arquivos dessa pasta
        filesQuery = filesQuery.eq("folder_id", folderId);
      }

      const { data: filesData, error: filesError } = await filesQuery;

      if (filesError) {
        console.error('Error fetching files:', filesError);
        toast.error(`Erro ao carregar arquivos: ${filesError.message}`);
        return;
      }

      console.log('Fetched files:', filesData);

      // Atualizar o estado com os dados obtidos
      setClientFolders(Array.isArray(foldersData) ? foldersData : []);
      setClientFiles(Array.isArray(filesData) ? filesData : []);
    } catch (error: any) {
      console.error('Error in fetchFilesAndFolders:', error);
      toast.error(`Erro ao carregar arquivos: ${error.message}`);
    }
  }, [clientId]);
  
  // Fetch social accounts with forced refresh - MELHORADO
  const fetchSocialAccounts = useCallback(async (forceRefresh = false) => {
    if (!clientId) return;
    
    try {
      setIsLoadingSocial(true);
      console.log('🔄 Buscando contas sociais para cliente:', clientId, forceRefresh ? '(forçando refresh)' : '');
      
      const { data, error } = await supabase
        .from('social_accounts')
        .select('*')
        .eq('client_id', clientId);
        
      if (error) {
        console.error('💥 Erro ao buscar contas sociais:', error);
        throw error;
      }
      
      console.log('📊 Contas sociais encontradas:', data);
      setSocialAccounts(data || []);
      
      // Show success message if we found accounts and this is a refresh
      if (forceRefresh && data && data.length > 0) {
        toast.success("Contas sociais atualizadas!");
      }
    } catch (error: any) {
      console.error('💥 Error fetching social accounts:', error);
      toast.error(`Erro ao carregar contas sociais: ${error.message}`);
    } finally {
      setIsLoadingSocial(false);
    }
  }, [clientId]);
  
  // Connect Instagram account - MELHORADO
  const handleConnectInstagram = () => {
    const CLIENT_ID = "1091096916228788";
    const REDIRECT_URI = "https://minhagencia.digital/auth/facebook/callback";
    
    console.log('🚀 Iniciando conexão para cliente:', clientId);
    
    if (!clientId) {
      console.error('❌ Client ID não encontrado!');
      toast.error("Erro: ID do cliente não encontrado");
      return;
    }
    
    // Store client ID for after OAuth return
    console.log('💾 Salvando dados OAuth no localStorage:', {
      oauth_client_id: clientId,
      oauth_platform: 'instagram'
    });
    
    localStorage.setItem("oauth_client_id", clientId);
    localStorage.setItem("oauth_platform", "instagram");
    
    // Verificar se foi salvo corretamente
    const savedClientId = localStorage.getItem("oauth_client_id");
    const savedPlatform = localStorage.getItem("oauth_platform");
    console.log('✅ Dados OAuth salvos:', { savedClientId, savedPlatform });
    
    const authUrl = new URL("https://www.facebook.com/v18.0/dialog/oauth");
    authUrl.searchParams.append("client_id", CLIENT_ID);
    authUrl.searchParams.append("redirect_uri", REDIRECT_URI);
    authUrl.searchParams.append("scope", "instagram_basic,instagram_content_publish,pages_show_list,pages_read_engagement");
    authUrl.searchParams.append("response_type", "code");
    authUrl.searchParams.append("state", `client_${clientId}`);
    
    console.log('🌐 URL de autorização gerada:', authUrl.toString());
    console.log('🔄 Redirecionando para Facebook OAuth...');
    
    window.location.href = authUrl.toString();
  };
  
  // Disconnect social account
  const handleDisconnectAccount = async (accountId: string) => {
    try {
      console.log('Desconectando conta:', accountId);
      
      const { error } = await supabase
        .from('social_accounts')
        .delete()
        .eq('id', accountId);
        
      if (error) throw error;
      
      await fetchSocialAccounts();
      toast.success("Conta desconectada com sucesso!");
    } catch (error: any) {
      console.error('Error disconnecting account:', error);
      toast.error(`Erro ao desconectar conta: ${error.message}`);
    }
  };
  
  // Navigate to folder
  const navigateToFolder = async (folderId: string | null, folderName: string) => {
    console.log('Navigating to folder:', folderId, folderName);
    setCurrentFolderId(folderId);
    
    if (folderId === null) {
      // Back to root
      setBreadcrumbs([{id: null, name: 'Raíz'}]);
    } else {
      // Add folder to breadcrumbs
      const newBreadcrumbs = [...breadcrumbs.filter(b => b.id !== folderId), {id: folderId, name: folderName}];
      setBreadcrumbs(newBreadcrumbs);
    }
    
    await fetchFilesAndFolders(folderId);
  };
  
  // Create folder
  const handleCreateFolder = async (name: string, parentFolderId: string | null) => {
    if (!clientId) return;

    try {
      console.log('Creating folder:', name, 'in parent:', parentFolderId, 'for client:', clientId);
      
      // Insert folder
      const { data, error } = await supabase
        .from("client_folders")
        .insert({
          name,
          client_id: clientId,
          parent_folder_id: parentFolderId,
        })
        .select();

      if (error) {
        console.error('Error creating folder:', error);
        toast.error(`Erro ao criar pasta: ${error.message}`);
        return;
      }

      console.log('Folder created successfully:', data);
      
      // Refresh folders
      await fetchFilesAndFolders(currentFolderId);
      toast.success("Pasta criada com sucesso!");
    } catch (error: any) {
      console.error('Error in handleCreateFolder:', error);
      toast.error(`Erro ao criar pasta: ${error.message}`);
    }
  };
  
  // Navigate to breadcrumb
  const navigateToBreadcrumb = async (index: number) => {
    const targetBreadcrumb = breadcrumbs[index];
    await navigateToFolder(targetBreadcrumb.id, targetBreadcrumb.name);
    setBreadcrumbs(breadcrumbs.slice(0, index + 1));
  };
  
  useEffect(() => {
    console.log('📍 Cliente ID:', clientId);
    fetchClientData();
    fetchFilesAndFolders(null);
    fetchSocialAccounts();
  }, [clientId, fetchFilesAndFolders, fetchSocialAccounts]);

  // Melhorar o monitoramento de mudanças
  useEffect(() => {
    if (!clientId) return;
    
    // Check for social accounts every 5 seconds (reduzido de 3 para 5)
    const interval = setInterval(() => {
      fetchSocialAccounts();
    }, 5000);
    
    // Also check when the window regains focus (user returns from OAuth)
    const handleFocus = () => {
      console.log('🔄 Janela ganhou foco - verificando contas sociais...');
      fetchSocialAccounts(true);
    };
    
    // Check when page becomes visible again
    const handleVisibilityChange = () => {
      if (!document.hidden) {
        console.log('🔄 Página ficou visível - verificando contas sociais...');
        fetchSocialAccounts(true);
      }
    };
    
    window.addEventListener('focus', handleFocus);
    document.addEventListener('visibilitychange', handleVisibilityChange);
    
    return () => {
      clearInterval(interval);
      window.removeEventListener('focus', handleFocus);
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, [clientId, fetchSocialAccounts]);
  
  // Go back if client doesn't exist
  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-screen">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#0448FF]"></div>
        <span className="ml-3">Carregando...</span>
      </div>
    );
  }
  
  if (!client) {
    navigate("/clients");
    return null;
  }
  
  const getStatusBadge = (status: string) => {
    switch (status) {
      case "active":
        return <Badge className="bg-green-500 hover:bg-green-600">Ativo</Badge>;
      case "inactive":
        return <Badge variant="outline" className="text-muted-foreground">Inativo</Badge>;
      case "pending":
        return <Badge variant="secondary" className="bg-amber-500 hover:bg-amber-600 text-white">Pendente</Badge>;
      default:
        return null;
    }
  };
  
  const handleLogoChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    
    setLogoFile(file);
    
    const reader = new FileReader();
    reader.onload = (event) => {
      if (event.target?.result) {
        setLogoPreview(event.target.result.toString());
      }
    };
    reader.readAsDataURL(file);
  };
  
  const handleSaveClientData = async () => {
    if (!client) return;
    
    try {
      let logoUrl = client.logoUrl;
      
      // Upload logo if changed
      if (logoFile) {
        const fileName = `${Date.now()}_${logoFile.name}`;
        const filePath = `logos/${fileName}`;
        
        const { error: uploadError } = await supabase.storage
          .from('client-files')
          .upload(filePath, logoFile);
          
        if (uploadError) {
          throw uploadError;
        }
        
        // Get public URL for the uploaded file
        const { data: { publicUrl } } = supabase.storage
          .from('client-files')
          .getPublicUrl(filePath);
          
        logoUrl = publicUrl;
      }
      
      // Update client data
      const { error } = await supabase
        .from('clients')
        .update({
          name: clientData.name,
          email: clientData.email || null,
          phone: clientData.phone || null,
          address: clientData.address || null,
          contact_name: clientData.contactName || null,
          contact_position: clientData.contactPosition || null,
          logo_url: logoUrl,
          updated_at: new Date().toISOString()
        })
        .eq('id', client.id);
        
      if (error) {
        throw error;
      }
      
      // Update local state
      setClient({
        ...client,
        name: clientData.name,
        email: clientData.email,
        phone: clientData.phone,
        address: clientData.address,
        contactName: clientData.contactName,
        contactPosition: clientData.contactPosition,
        logoUrl: logoUrl
      });
      
      toast.success("Dados do cliente atualizados com sucesso!");
      setIsEditing(false);
    } catch (error: any) {
      console.error('Error updating client data:', error);
      toast.error(`Erro ao atualizar dados do cliente: ${error.message}`);
    }
  };
  
  // Handle file upload trigger from context menu
  const handleUploadTrigger = () => {
    if (fileInputRef.current) {
      fileInputRef.current.click();
    }
  };
  
  // Handle file upload with dragging
  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(true);
  };
  
  const handleDragLeave = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
  };
  
  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
    
    const files = e.dataTransfer.files;
    if (files && files.length > 0) {
      handleFileUpload(files);
    }
  };
  
  const handleFileUpload = async (files: FileList | File[]) => {
    if (!files.length || !clientId) return;
    
    try {
      console.log('Uploading files to folder:', currentFolderId, 'for client:', clientId);
      
      // Process each uploaded file
      for (const file of Array.from(files)) {
        // Get file extension
        const extension = file.name.split('.').pop()?.toLowerCase() || '';
        let fileType = 'document';
        
        if (['jpg', 'jpeg', 'png', 'gif', 'webp', 'svg'].includes(extension)) {
          fileType = 'image';
        } else if (['zip', 'rar', '7z'].includes(extension)) {
          fileType = 'archive';
        } else if (['ai', 'psd', 'xd', 'sketch', 'figma'].includes(extension)) {
          fileType = 'design';
        }

        // Create path for file
        const filePath = currentFolderId 
          ? `files/${clientId}/${currentFolderId}/${Date.now()}_${file.name}`
          : `files/${clientId}/${Date.now()}_${file.name}`;

        // Upload file to storage
        const { error: uploadError } = await supabase.storage
          .from('client-files')
          .upload(filePath, file);

        if (uploadError) {
          console.error('File upload error:', uploadError);
          toast.error(`Erro ao fazer upload do arquivo: ${uploadError.message}`);
          continue;
        }

        // Get public URL for the uploaded file
        const { data: { publicUrl } } = supabase.storage
          .from('client-files')
          .getPublicUrl(filePath);
        
        console.log('File uploaded successfully:', publicUrl);

        // Defensive: if file.size is undefined/null, log special error
        if (typeof file.size !== 'number') {
          toast.error('Erro crítico: tamanho do arquivo não está disponível.');
          console.error('file.size indefinido para', file.name, file);
          continue;
        }

        // Add file record to the database WITH size_bytes populated
        const { data: fileData, error: insertError } = await supabase
          .from('client_files')
          .insert({
            client_id: clientId,
            name: file.name,
            file_type: fileType,
            size: `${(file.size / (1024 * 1024)).toFixed(1)} MB`,
            size_bytes: file.size,
            url: publicUrl,
            date_added: new Date().toISOString(),
            folder_id: currentFolderId,
            path: filePath
          })
          .select();

        if (insertError) {
          // Log the detailed error for easier troubleshooting
          console.error('File insert error:', insertError);
          toast.error(`Erro ao salvar registro no banco: ${insertError.message}`);
          continue;
        }
        
        console.log('File record created:', fileData);
      }
      
      // Refresh files
      await fetchFilesAndFolders(currentFolderId);
      toast.success(`${files.length} arquivo(s) adicionado(s) com sucesso!`);
    } catch (error: any) {
      // Show the full error for easier debugging
      console.error('Error uploading files:', error);
      toast.error(`Erro ao fazer upload de arquivos: ${error.message || error}`);
    }
  };
  
  const handleDeleteFile = async (fileId: string) => {
    if (!client) return;
    
    try {
      // Get file URL first to delete from storage
      const file = clientFiles.find(f => f.id === fileId);
      if (!file) return;
      
      console.log('Deleting file:', file);
      
      // Delete file record from database
      const { error } = await supabase
        .from('client_files')
        .delete()
        .eq('id', fileId);
        
      if (error) {
        throw error;
      }
      
      // Update state
      setClientFiles(clientFiles.filter(file => file.id !== fileId));
      toast.success("Arquivo removido com sucesso!");
    } catch (error: any) {
      console.error('Error deleting file:', error);
      toast.error(`Erro ao remover arquivo: ${error.message}`);
    }
  };
  
  const getFileIcon = (type: string) => {
    switch (type) {
      case 'image':
        return <ImageIcon className="h-4 w-4 text-blue-500" />;
      case 'archive':
        return <Folder className="h-4 w-4 text-amber-500" />;
      case 'design':
        return <ImageIcon className="h-4 w-4 text-purple-500" />;
      default:
        return <FileText className="h-4 w-4 text-gray-500" />;
    }
  };
  
  return (
    <div className="space-y-6">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div className="flex items-center">
          <Button variant="ghost" onClick={() => navigate("/clients")} className="mr-2">
            <ArrowLeft className="h-4 w-4" />
          </Button>
          <h1 className="text-3xl font-bold tracking-tight">{client.name}</h1>
          {getStatusBadge(client.status)}
        </div>
        
        {!isEditing ? (
          <Button onClick={() => setIsEditing(true)} className="bg-[#0448FF]">
            <Edit className="mr-2 h-4 w-4" />
            Editar Cliente
          </Button>
        ) : (
          <div className="flex gap-2">
            <Button variant="outline" onClick={() => setIsEditing(false)}>
              <X className="mr-2 h-4 w-4" />
              Cancelar
            </Button>
            <Button onClick={handleSaveClientData} className="bg-[#0448FF]">
              <Save className="mr-2 h-4 w-4" />
              Salvar
            </Button>
          </div>
        )}
      </div>
      
      <Tabs defaultValue="info" className="space-y-4">
        <TabsList>
          <TabsTrigger value="info">Informações</TabsTrigger>
          <TabsTrigger value="accounts">Contas</TabsTrigger>
          <TabsTrigger value="files">Arquivos</TabsTrigger>
        </TabsList>
        
        <TabsContent value="info">
          <Card>
            <CardHeader>
              <CardTitle>Informações do Cliente</CardTitle>
              <CardDescription>
                Informações gerais e contatos do cliente
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div className="space-y-6">
                  <div className="flex items-center gap-4">
                    <Avatar className="h-24 w-24">
                      {logoPreview ? (
                        <AvatarImage src={logoPreview} alt={client.name} />
                      ) : (
                        <AvatarFallback className="bg-[#0448FF] text-white text-2xl">
                          {client.logoInitials}
                        </AvatarFallback>
                      )}
                    </Avatar>
                    {isEditing && (
                      <div>
                        <Label htmlFor="logo-upload" className="cursor-pointer">
                          <div className="flex items-center gap-2 text-sm text-[#0448FF] hover:underline mb-1">
                            <Upload className="h-3.5 w-3.5" />
                            Alterar logo
                          </div>
                        </Label>
                        <Input 
                          id="logo-upload" 
                          type="file" 
                          accept="image/*" 
                          className="hidden"
                          onChange={handleLogoChange}
                        />
                      </div>
                    )}
                  </div>
                  
                  <div className="space-y-4">
                    <div>
                      <Label htmlFor="name">Nome da empresa</Label>
                      <Input 
                        id="name" 
                        value={clientData.name} 
                        onChange={e => setClientData({...clientData, name: e.target.value})} 
                        disabled={!isEditing}
                        className="mt-1"
                      />
                    </div>
                    
                    <div>
                      <Label htmlFor="email">Email</Label>
                      <Input 
                        id="email" 
                        value={clientData.email} 
                        onChange={e => setClientData({...clientData, email: e.target.value})} 
                        disabled={!isEditing}
                        className="mt-1"
                      />
                    </div>
                    
                    <div>
                      <Label htmlFor="phone">Telefone</Label>
                      <Input 
                        id="phone" 
                        value={clientData.phone} 
                        onChange={e => setClientData({...clientData, phone: e.target.value})} 
                        disabled={!isEditing}
                        className="mt-1"
                      />
                    </div>
                  </div>
                </div>
                
                <div className="space-y-4">
                  <div>
                    <Label htmlFor="address">Endereço</Label>
                    <Input 
                      id="address" 
                      value={clientData.address} 
                      onChange={e => setClientData({...clientData, address: e.target.value})} 
                      disabled={!isEditing}
                      className="mt-1"
                    />
                  </div>
                  
                  <div>
                    <Label htmlFor="contactName">Nome do contato</Label>
                    <Input 
                      id="contactName" 
                      value={clientData.contactName} 
                      onChange={e => setClientData({...clientData, contactName: e.target.value})} 
                      disabled={!isEditing}
                      className="mt-1"
                    />
                  </div>
                  
                  <div>
                    <Label htmlFor="contactPosition">Cargo do contato</Label>
                    <Input 
                      id="contactPosition" 
                      value={clientData.contactPosition} 
                      onChange={e => setClientData({...clientData, contactPosition: e.target.value})} 
                      disabled={!isEditing}
                      className="mt-1"
                    />
                  </div>
                  
                  <div className="pt-4">
                    <div className="flex justify-between text-sm text-muted-foreground">
                      <span>Tarefas pendentes:</span>
                      <span>{client.pendingTasks}</span>
                    </div>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>
        </TabsContent>
        
        <TabsContent value="accounts">
          <Card>
            <CardHeader>
              <CardTitle>Contas Sociais</CardTitle>
              <CardDescription>
                Conecte e gerencie as contas sociais do cliente (Facebook + Instagram)
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="space-y-6">
                {/* Instagram + Facebook Connection */}
                <div className="flex items-center justify-between p-4 border rounded-lg">
                  <div className="flex items-center gap-3">
                    <div className="flex items-center">
                      <Facebook className="h-8 w-8 text-blue-600" />
                      <Instagram className="h-6 w-6 text-pink-600 -ml-2" />
                    </div>
                    <div>
                      <h3 className="font-semibold">Facebook + Instagram</h3>
                      <p className="text-sm text-muted-foreground">
                        Conecte uma página do Facebook com Instagram vinculado
                      </p>
                    </div>
                  </div>
                  
                  <div className="flex items-center gap-2">
                    {socialAccounts.length > 0 ? (
                      <>
                        <Badge className="bg-green-100 text-green-800">
                          <CheckCircle className="h-3 w-3 mr-1" />
                          Conectado
                        </Badge>
                        <Button 
                          variant="outline" 
                          size="sm"
                          onClick={() => {
                            if (socialAccounts[0]) handleDisconnectAccount(socialAccounts[0].id);
                          }}
                        >
                          Desconectar
                        </Button>
                      </>
                    ) : (
                      <>
                        <Badge variant="secondary">
                          <AlertCircle className="h-3 w-3 mr-1" />
                          Não conectado
                        </Badge>
                        <Button 
                          onClick={handleConnectInstagram}
                          className="bg-gradient-to-r from-blue-600 to-pink-600 hover:from-blue-700 hover:to-pink-700"
                          size="sm"
                        >
                          <Facebook className="h-4 w-4 mr-1" />
                          <Instagram className="h-4 w-4 mr-2" />
                          Conectar
                        </Button>
                      </>
                    )}
                  </div>
                </div>

                {/* Connected Accounts Details */}
                {socialAccounts.length > 0 && (
                  <div className="mt-6">
                    <h4 className="font-semibold mb-3">Contas Conectadas</h4>
                    <div className="space-y-3">
                      {socialAccounts.map((account) => (
                        <div key={account.id} className="flex items-center justify-between p-4 bg-gradient-to-r from-blue-50 to-pink-50 rounded-lg border">
                          <div className="flex items-center gap-4">
                            <div className="flex items-center">
                              <Facebook className="h-6 w-6 text-blue-600" />
                              <Instagram className="h-5 w-5 text-pink-600 -ml-1" />
                            </div>
                            <div>
                              <div className="flex items-center gap-2">
                                <p className="font-medium text-lg">{account.account_name}</p>
                                <Badge variant="outline" className="text-xs">
                                  Facebook + Instagram
                                </Badge>
                              </div>
                              <div className="flex items-center gap-4 text-sm text-muted-foreground">
                                <span>
                                  Conectado em {new Date(account.created_at).toLocaleDateString('pt-BR')}
                                </span>
                                {account.followers_count && (
                                  <span className="flex items-center gap-1">
                                    <Instagram className="h-3 w-3" />
                                    {account.followers_count.toLocaleString('pt-BR')} seguidores
                                  </span>
                                )}
                              </div>
                              {account.account_url && (
                                <a 
                                  href={account.account_url} 
                                  target="_blank" 
                                  rel="noopener noreferrer"
                                  className="text-xs text-blue-600 hover:underline"
                                >
                                  Ver perfil →
                                </a>
                              )}
                            </div>
                          </div>
                          <Button 
                            variant="ghost" 
                            size="sm"
                            onClick={() => handleDisconnectAccount(account.id)}
                            className="text-red-600 hover:text-red-700 hover:bg-red-50"
                          >
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {isLoadingSocial && (
                  <div className="text-center py-8">
                    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#0448FF] mx-auto"></div>
                    <p className="text-sm text-muted-foreground mt-3">Carregando contas...</p>
                  </div>
                )}

                {socialAccounts.length === 0 && !isLoadingSocial && (
                  <div className="text-center py-8 text-muted-foreground">
                    <div className="flex justify-center mb-3">
                      <Facebook className="h-8 w-8 text-blue-600 mr-1" />
                      <Instagram className="h-8 w-8 text-pink-600" />
                    </div>
                    <p>Nenhuma conta conectada ainda.</p>
                    <p className="text-sm mt-1">Clique em "Conectar" para vincular uma página do Facebook com Instagram.</p>
                  </div>
                )}
              </div>
            </CardContent>
          </Card>
        </TabsContent>
        
        <TabsContent value="files">
          <Card>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <div>
                <CardTitle>Arquivos do Cliente</CardTitle>
                <CardDescription className="mt-1">
                  Armazene e organize todos os arquivos do cliente
                </CardDescription>
              </div>
              <div className="flex space-x-2">
                <Button 
                  variant="outline" 
                  onClick={() => setIsFolderDialogOpen(true)}
                  className="flex items-center"
                >
                  <FolderPlus className="mr-2 h-4 w-4" />
                  Nova Pasta
                </Button>
                <Label htmlFor="file-upload" className="cursor-pointer">
                  <Button className="bg-[#0448FF]">
                    <Upload className="mr-2 h-4 w-4" />
                    Upload
                  </Button>
                </Label>
                <Input 
                  id="file-upload" 
                  ref={fileInputRef}
                  type="file" 
                  multiple
                  onChange={(e) => e.target.files && handleFileUpload(e.target.files)}
                  className="hidden" 
                />
              </div>
            </CardHeader>
            <CardContent>
              <CreateFolderDialog
                currentFolderId={currentFolderId}
                onCreateFolder={handleCreateFolder}
                open={isFolderDialogOpen}
                onOpenChange={setIsFolderDialogOpen}
              />
              
              {/* Breadcrumbs */}
              <div className="flex items-center text-sm mb-4 overflow-x-auto pb-2">
                {breadcrumbs.map((crumb, index) => (
                  <div key={index} className="flex items-center">
                    {index > 0 && <ChevronRight className="h-4 w-4 mx-1 text-muted-foreground" />}
                    <Button 
                      variant="ghost" 
                      className="p-1 h-auto text-sm"
                      onClick={() => navigateToBreadcrumb(index)}
                    >
                      {crumb.name}
                    </Button>
                  </div>
                ))}
              </div>
              
              <FilesContextMenu 
                onCreateFolder={() => setIsFolderDialogOpen(true)}
                onUploadFiles={handleUploadTrigger}
              >
                <div 
                  className={`min-h-[300px] border-2 border-dashed rounded-lg ${isDragging ? 'border-blue-500 bg-blue-50/50' : 'border-gray-200'}`}
                  onDragOver={handleDragOver}
                  onDragLeave={handleDragLeave}
                  onDrop={handleDrop}
                >
                  {clientFolders.length === 0 && clientFiles.length === 0 ? (
                    <div className="text-center py-8">
                      <Folder className="h-10 w-10 text-muted-foreground mx-auto mb-2" />
                      <p className="text-muted-foreground">
                        Nenhum arquivo encontrado. Arraste arquivos ou clique em Upload.
                      </p>
                    </div>
                  ) : (
                    <div className="space-y-2">
                      {/* Folders */}
                      {clientFolders.map((folder) => (
                        <div 
                          key={folder.id} 
                          className="flex items-center justify-between p-3 rounded-lg border hover:bg-gray-50 transition-colors"
                        >
                          <div 
                            className="flex items-center gap-3 cursor-pointer flex-1"
                            onClick={() => navigateToFolder(folder.id, folder.name)}
                          >
                            <Folder className="h-5 w-5 text-amber-500" />
                            <div>
                              <div className="font-medium text-sm">{folder.name}</div>
                            </div>
                          </div>
                          <div className="flex items-center gap-2">
                            <Dialog>
                              <DialogTrigger asChild>
                                <Button variant="ghost" size="icon" className="h-8 w-8 text-red-600">
                                  <Trash2 className="h-4 w-4" />
                                </Button>
                              </DialogTrigger>
                              <DialogContent>
                                <DialogHeader>
                                  <DialogTitle>Confirmar exclusão</DialogTitle>
                                  <DialogDescription>
                                    Tem certeza que deseja excluir a pasta "{folder.name}"? Esta ação não pode ser desfeita e todos os arquivos dentro da pasta serão excluídos.
                                  </DialogDescription>
                                </DialogHeader>
                                <DialogFooter>
                                  <Button variant="outline" onClick={() => {}}>Cancelar</Button>
                                  <Button 
                                    variant="destructive"
                                    onClick={() => {}}
                                  >
                                    Excluir
                                  </Button>
                                </DialogFooter>
                              </DialogContent>
                            </Dialog>
                          </div>
                        </div>
                      ))}
                      
                      {/* Files */}
                      {clientFiles.map((file) => (
                        <div 
                          key={file.id} 
                          className="flex items-center justify-between p-3 rounded-lg border hover:bg-gray-50 transition-colors"
                        >
                          <div className="flex items-center gap-3">
                            {getFileIcon(file.file_type)}
                            <div>
                              <div className="font-medium text-sm">{file.name}</div>
                              <div className="text-xs text-muted-foreground">
                                {file.size} • Adicionado em {new Date(file.date_added).toLocaleDateString('pt-BR')}
                              </div>
                            </div>
                          </div>
                          <div className="flex items-center gap-2">
                            <Button variant="ghost" size="icon" className="h-8 w-8 text-blue-600" onClick={() => window.open(file.url, '_blank')}>
                              <FileText className="h-4 w-4" />
                            </Button>
                            <Dialog>
                              <DialogTrigger asChild>
                                <Button variant="ghost" size="icon" className="h-8 w-8 text-red-600">
                                  <Trash2 className="h-4 w-4" />
                                </Button>
                              </DialogTrigger>
                              <DialogContent>
                                <DialogHeader>
                                  <DialogTitle>Confirmar exclusão</DialogTitle>
                                  <DialogDescription>
                                    Tem certeza que deseja excluir o arquivo "{file.name}"? Esta ação não pode ser desfeita.
                                  </DialogDescription>
                                </DialogHeader>
                                <DialogFooter>
                                  <Button variant="outline" onClick={() => {}}>Cancelar</Button>
                                  <Button 
                                    variant="destructive" 
                                    onClick={() => handleDeleteFile(file.id)}
                                  >
                                    Excluir
                                  </Button>
                                </DialogFooter>
                              </DialogContent>
                            </Dialog>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </FilesContextMenu>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
}
